/**
 * One message's body, and its attachments.
 *
 * HTML mail is rendered as it was designed, inside a sandboxed iframe: no scripts (the sandbox has
 * no allow-scripts, and a CSP inside the document says the same), no forms, links open in a new
 * tab. allow-same-origin is there only so this page can measure the document's height — without
 * script execution the document cannot use that origin for anything.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { FileText, Loader2, Paperclip } from 'lucide-react'
import { inboxApi } from '../api/inboxApi'
import type { MessageAttachment, ThreadMessage } from '../types'

const CSP =
  "default-src 'none'; img-src https: http: data: cid:; style-src 'unsafe-inline' https:; font-src https: data:"

function srcDoc(html: string) {
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="${CSP}">
<base target="_blank">
<style>
  html,body{margin:0;padding:0;background:transparent;color:#1f1f1f;
    font:14px/1.55 -apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;overflow-wrap:anywhere}
  img{max-width:100%;height:auto}
  table{max-width:100%}
  blockquote{margin:0 0 0 .8em;padding-left:.8em;border-left:2px solid #ddd;color:#666}
</style></head><body>${html}</body></html>`
}

function HtmlBody({ html }: { html: string }) {
  const ref = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(120)

  const measure = useCallback(() => {
    const doc = ref.current?.contentDocument
    if (doc?.documentElement) setHeight(Math.min(Math.max(doc.documentElement.scrollHeight, 40), 4000))
  }, [])

  useEffect(() => {
    const frame = ref.current
    if (!frame) return
    let observer: ResizeObserver | null = null
    const onLoad = () => {
      measure()
      const body = frame.contentDocument?.body
      if (body && 'ResizeObserver' in window) {
        observer = new ResizeObserver(measure)
        observer.observe(body)
      }
    }
    frame.addEventListener('load', onLoad)
    return () => {
      frame.removeEventListener('load', onLoad)
      observer?.disconnect()
    }
  }, [html, measure])

  return (
    <iframe
      ref={ref}
      title="Email"
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={srcDoc(html)}
      className="w-full rounded bg-white"
      style={{ height }}
    />
  )
}

function size(bytes: number | null) {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function AttachmentChip({ threadId, messageId, attachment }: { threadId: number; messageId: number; attachment: MessageAttachment }) {
  const { toast } = useToast()
  const [busy, setBusy] = useState(false)
  const open = async () => {
    // Open the tab synchronously (inside the click) so popup blockers allow it, then point it at
    // the signed link once we have it.
    const tab = window.open('', '_blank')
    setBusy(true)
    try {
      const link = await inboxApi.attachmentLink(threadId, messageId, attachment.id)
      if (tab) tab.location.href = link.url
      else window.location.href = link.url
    } catch {
      tab?.close()
      toast({ title: 'Could not open the attachment', variant: 'destructive' })
    } finally {
      setBusy(false)
    }
  }
  return (
    <Button variant="outline" size="sm" className="h-8 max-w-full gap-1.5" onClick={open} disabled={busy}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileText className="h-3.5 w-3.5" />}
      <span className="truncate">{attachment.filename || 'attachment'}</span>
      {attachment.size ? <span className="text-xs text-muted-foreground">{size(attachment.size)}</span> : null}
    </Button>
  )
}

export function MessageBody({ threadId, message }: { threadId: number; message: ThreadMessage }) {
  return (
    <div className="space-y-3">
      {message.html ? (
        <HtmlBody html={message.html} />
      ) : (
        <p className="whitespace-pre-wrap text-sm">{message.text}</p>
      )}
      {message.attachments?.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
          {message.attachments.map((a) => (
            <AttachmentChip key={a.id} threadId={threadId} messageId={message.id} attachment={a} />
          ))}
        </div>
      )}
    </div>
  )
}
